import * as React from 'react';

import { EyeIcon, EyeOffIcon } from '../../../icons';
import { cx } from '../../../utils/cx';

import HintText from './HintText';
import { InputBase } from './Input';
import { TextField } from './Input';
import Label from './Label';

import type { IInputBaseProps } from './Input';

interface IPasswordInputProps extends Omit<IInputBaseProps, 'type' | 'icon'> {
  /** Label text for the input */
  label?: string;
  /** Helper/error text displayed below the input */
  hint?: React.ReactNode;
  /** Whether to hide required indicator from label */
  hideRequiredIndicator?: boolean;
}

const PasswordInput = ({
  label,
  hint,
  hideRequiredIndicator,
  className,
  ref,
  groupRef,
  wrapperClassName,
  ...props
}: IPasswordInputProps) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const toggleVisibility = React.useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const ToggleIcon = showPassword ? EyeOffIcon : EyeIcon;

  return (
    <TextField
      aria-label={!label ? 'Password' : undefined}
      {...props}
      type={showPassword ? 'text' : 'password'}
      className={className}
    >
      {({ isRequired, isInvalid, isDisabled }) => (
        <>
          {label && <Label isRequired={hideRequiredIndicator ? false : isRequired}>{label}</Label>}

          <div className="relative w-full">
            <InputBase
              {...{
                ref,
                groupRef,
                wrapperClassName,
                isInvalid,
                isDisabled,
              }}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={toggleVisibility}
              className={cx(
                'text-fg-quaternary hover:text-fg-quaternary_hover absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer transition duration-200',
                isDisabled && 'text-fg-disabled pointer-events-none'
              )}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <ToggleIcon className="size-4" />
            </button>
          </div>

          {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
        </>
      )}
    </TextField>
  );
};

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
